import React from 'react';
import AIStorageMarketChart from './AIStorageMarketChart';
import ChipComputeCapacityChart from './ChipComputeCapacityChart';
import DataCenterBuildoutExplorer from './DataCenterBuildoutExplorer';
import HyperscalerCapexChart from './HyperscalerCapexChart';
import MetricSection from './MetricSection';
import SourceFooter from './SourceFooter';
import TimeSeriesChart from './TimeSeriesChart';
import { ChartPanel, datasetById, MetricCallout, SnapshotBars } from './SectionElements';

const ComputeMarketSection = ({ datasets, range, isZh = false }) => {
    const capacity = datasetById(datasets, 'epoch_data_center_capacity');
    const owners = datasetById(datasets, 'epoch_data_center_owner_capacity');
    const projects = datasetById(datasets, 'epoch_data_center_projects');
    const chipCompute = datasetById(datasets, 'epoch_chip_sales_h100e');
    const chips = datasetById(datasets, 'epoch_accelerator_shipments');
    const dram = datasetById(datasets, 'dram_spot_price');
    const enterpriseSsd = datasetById(datasets, 'enterprise_ssd_vendor_revenue_1q26');
    const rentals = datasetById(datasets, 'gpu_rental_prices');
    const capex = datasetById(datasets, 'hyperscaler_capex');
    const latestCapacity = [...capacity.series].reverse().find((point) => point.status === 'tracked_operational_capacity');
    const latestB300 = [...chips.series].reverse().find((point) => point.chip === 'B300');

    return (
        <MetricSection
            id="compute-market"
            index={4}
            title="Compute Infrastructure and Market"
            titleZh="算力建设与市场"
            summary="Connect physical IT power, accelerator shipments, DRAM pricing and enterprise storage, hyperscaler capital intensity, ownership concentration and rental prices without pretending the tracked sample is the whole world."
            summaryZh="把数据中心 GW、加速卡出货、DRAM 价格与企业级存储、云厂商资本开支、所有者集中度和 GPU 租价放在一起，同时明确已追踪样本不等于全球总量。"
            isZh={isZh}
        >
            <div className="ai-dev-callout-row">
                <MetricCallout value={`${latestCapacity.value.toFixed(1)} GW`} label={isZh ? '已追踪投运 IT 容量' : 'tracked operational IT capacity'} note={`${latestCapacity.activeSites} active sites`} />
                <MetricCallout value={`${latestCapacity.h100EquivalentMillions.toFixed(1)}M`} label="H100 equivalents" note={`${latestCapacity.trackedSites} tracked sites`} />
                <MetricCallout value={`${(latestB300.value / 1e6).toFixed(2)}M`} label={isZh ? '最新季度 B300 估算' : 'latest-quarter B300 estimate'} note={latestB300.incomplete ? (isZh ? '季度未完结' : 'incomplete quarter') : latestB300.date} />
                <MetricCallout value={`$${enterpriseSsd.reportedTotal.toFixed(2)}B`} label={isZh ? '企业级 SSD 季度收入' : 'quarterly enterprise SSD revenue'} note={`1Q26 · +${enterpriseSsd.quarterGrowth.toFixed(1)}% QoQ`} />
            </div>
            <div className="ai-dev-grid ai-dev-grid--compute-top">
                <ChartPanel eyebrow={isZh ? '物理建设' : 'PHYSICAL BUILDOUT'} title={isZh ? 'AI 数据中心 IT 容量：已投运与建设规划' : 'AI data-center IT capacity: operational and planned'} note={isZh ? 'GW；实线为已投运，虚线为 Epoch 建设规划估算' : 'GW; solid is operational, dashed is Epoch buildout estimate'}>
                    <TimeSeriesChart
                        dataset={capacity}
                        range={range}
                        seriesKey="capacityType"
                        seriesLabelFormatter={(key) => ({
                            operational: isZh ? '已投运容量' : 'Operational capacity',
                            planned_estimate: isZh ? '建设中 / 规划预计' : 'Buildout estimate',
                        }[key] || key)}
                        dashedSeriesKeys={['planned_estimate']}
                        ariaLabel="Tracked operational and planned data center IT capacity"
                        valueFormatter={(value) => `${value.toFixed(1)} GW`}
                        zeroBaseline
                    />
                    <SourceFooter dataset={capacity} isZh={isZh} />
                </ChartPanel>
                <ChartPanel eyebrow={isZh ? '所有者结构' : 'OWNERSHIP'} title={isZh ? '当前容量归属' : 'Current tracked capacity by owner'} note={isZh ? '所有者 ≠ 模型公司使用者' : 'owner is not the same as user'}>
                    <SnapshotBars rows={owners.series} labelKey="owner" valueFormatter={(value) => `${value.toFixed(2)} GW`} color="#486e9d" />
                    <SourceFooter dataset={owners} isZh={isZh} />
                </ChartPanel>
            </div>
            <ChartPanel
                className="ai-dev-panel--primary"
                eyebrow={isZh ? '站点建设追踪' : 'PROJECT BUILDOUT TRACKER'}
                title={isZh ? '77 个 AI 数据中心建设详情' : 'Buildout detail for 77 AI data centers'}
                note={isZh ? '搜索、筛选并展开每个站点的建设时间线' : 'search, filter, and expand every site timeline'}
            >
                <DataCenterBuildoutExplorer dataset={projects} isZh={isZh} />
                <SourceFooter dataset={projects} isZh={isZh} />
            </ChartPanel>
            <ChartPanel
                className="ai-dev-panel--primary"
                eyebrow={isZh ? '交付算力' : 'DELIVERED COMPUTE'}
                title={isZh ? 'AI 芯片季度交付算力（H100 等效）' : 'Quarterly AI chip compute delivered in H100 equivalents'}
                note={isZh ? '按设计商堆叠 · 可切换季度新增 / 累计交付' : 'stacked by designer · switch between quarterly and cumulative delivery'}
            >
                <ChipComputeCapacityChart dataset={chipCompute} isZh={isZh} />
                <SourceFooter dataset={chipCompute} isZh={isZh} />
            </ChartPanel>
            <ChartPanel
                className="ai-dev-panel--primary"
                eyebrow={isZh ? '内存与存储' : 'MEMORY & STORAGE'}
                title={isZh ? 'DRAM 价格趋势与企业级 SSD 市场结构' : 'DRAM pricing and enterprise SSD market structure'}
                note={isZh ? '现货曲线与合约报价分清规格 · 内存和持久化存储分开呈现' : 'spot history and contract quotes separated by specification · memory and persistent storage shown distinctly'}
            >
                <AIStorageMarketChart dramDataset={dram} ssdDataset={enterpriseSsd} isZh={isZh} />
                <div className="ai-dev-storage-chart__sources">
                    <SourceFooter dataset={dram} isZh={isZh} />
                    <SourceFooter dataset={enterpriseSsd} isZh={isZh} />
                </div>
            </ChartPanel>
            <ChartPanel eyebrow={isZh ? '芯片数量' : 'ACCELERATOR UNITS'} title={isZh ? '英伟达 AI 加速卡季度出货数量估算' : 'Estimated quarterly Nvidia AI accelerator units'} note={isZh ? '不同型号数量不可直接等同算力；最新季度未完结' : 'unit counts are not compute-equivalent; latest quarter incomplete'}>
                <TimeSeriesChart dataset={chips} range={range} seriesKey="chip" ariaLabel="Estimated Nvidia accelerator shipments" valueFormatter={(value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value)} />
                <SourceFooter dataset={chips} isZh={isZh} />
            </ChartPanel>
            <ChartPanel className="ai-dev-panel--primary" eyebrow={isZh ? '资本开支' : 'CAPITAL INTENSITY'} title={isZh ? '主要云厂商 CapEx 与未来预测' : 'Hyperscaler CapEx and outlook'} note={isZh ? '可切换季度 / 年度 · 实际与预测分开标识' : 'quarterly / annual · actuals and estimates separated'}>
                <HyperscalerCapexChart dataset={capex} isZh={isZh} />
                <SourceFooter dataset={capex} isZh={isZh} />
            </ChartPanel>
            <ChartPanel eyebrow={isZh ? '算力市场价格' : 'COMPUTE MARKET PRICE'} title={isZh ? 'GPU 租赁价格走势' : 'GPU rental price trend'} note={isZh ? '美元 / GPU-小时 · 月末指数' : 'USD / GPU-hour · month-end index'}>
                <TimeSeriesChart
                    dataset={rentals}
                    range={range}
                    seriesKey="accelerator"
                    ariaLabel="GPU rental price index history"
                    valueFormatter={(value) => `$${value.toFixed(2)}`}
                />
                <SourceFooter dataset={rentals} isZh={isZh} />
            </ChartPanel>
        </MetricSection>
    );
};

export default ComputeMarketSection;
